import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Stack,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import {
  useGetcampaigndetailbyidQuery,
  useGetcampaignlistQuery,
  useGetRcsTemplateViewQuery,
  useGetrcsclickreportQuery,
  useLazyRcsdeliveryreportdownloadzipopupQuery,

} from "../store/services/rcstemplateApi";
import { useCheckFailedRefundQuery } from "../store/services/reportApi";
import dayjs from "dayjs";
import { toast } from "react-toastify";
const RcscampaignList = () => {
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [detailsPage, setDetailsPage] = useState(1);
  const [page, setPage] = useState(1);

  // Caching for campaign details
  const [cachedRcsCampaign, setCachedRcsCampaign] = useState(null);
  const [cachedRcsOverview, setCachedRcsOverview] = useState(null);
  const [rcsMessageData, setRcsMessageData] = useState([]);
  const [rcsPagination, setRcsPagination] = useState(null);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);

  // Click report states
  const [clickReportOpen, setClickReportOpen] = useState(false);
  const [clickReportMobile, setClickReportMobile] = useState(null);
  const [clickReportPage, setClickReportPage] = useState(1);
  const [clickReportRowsPerPage, setClickReportRowsPerPage] = useState(10);

  // API Calls
  const { data: rcsCampaign, isFetching: isCampaignFetching } = useGetcampaignlistQuery({
    page,
    limit: rowsPerPage,
  });

  const { data: templateData } = useGetRcsTemplateViewQuery(selectedTemplateId, {
    skip: !selectedTemplateId,
  });

  const { data: campaignDetails, isLoading: loadingDetails, isFetching: loadingDetailsFetching } =
    useGetcampaigndetailbyidQuery(
      {
        campaignId: selectedCampaignId,
        page: detailsPage,
        pagesize: rowsPerPage,
      },
      {
        skip: !selectedCampaignId || !shouldFetchDetails,
      }
    );

  const { data: failedRefundData } = useCheckFailedRefundQuery();
  const isFailedRefund = failedRefundData?.isFailedRefund === true;

  // Click report query
  const {
    data: rcsClickReport,
    isLoading: rcsClickReportLoading,
    isFetching: rcsClickReportFetching,
    error: rcsClickReportError,
  } = useGetrcsclickreportQuery(
    {
      campaign_id: selectedCampaignId,
      mobile_number: clickReportMobile,
      page: clickReportPage,
      limit: clickReportRowsPerPage,
    },
    { skip: !selectedCampaignId || !clickReportMobile }
  );

  // ZIP download lazy query
  const [triggerZipDownload, { isLoading: zipDownloadLoading }] =
    useLazyRcsdeliveryreportdownloadzipopupQuery();




  // Extract pagination info
  const pagination = rcsCampaign?.pagination;

  // Map campaign list data
  const rows = rcsCampaign?.data?.map((item) => ({
    id: item.sr_no,
    campaign_name: item.campaign_name,
    bot_name: item.bot_name,
    template_name: item.template_name,
    created_at: new Date(item.createdat).toLocaleString(),
    executed_at: new Date(item.executedat).toLocaleString(),
    status: item.status,
    campaign_id: item.campaign_id,
    template_id: item.template_id,
  })) || [];

  // Cache campaign details when fetched
  useEffect(() => {
    if (campaignDetails?.status && shouldFetchDetails && selectedCampaignId) {
      setCachedRcsCampaign(campaignDetails.campaign);
      setCachedRcsOverview(campaignDetails.overview);
      setRcsMessageData(campaignDetails.data || []);
      setRcsPagination(campaignDetails.pagination);
      setShouldFetchDetails(false);
    }
  }, [campaignDetails, shouldFetchDetails, selectedCampaignId]);

  // Update message data when page changes (using cache)
  useEffect(() => {
    if (campaignDetails?.status && selectedCampaignId && dialogOpen) {
      if (cachedRcsCampaign && cachedRcsCampaign.campaign_id === selectedCampaignId) {
        // Only update messages, keep campaign/overview cached
        setRcsMessageData(campaignDetails.data || []);
        setRcsPagination(campaignDetails.pagination);
      } else {
        // First load, cache everything
        setCachedRcsCampaign(campaignDetails.campaign);
        setCachedRcsOverview(campaignDetails.overview);
        setRcsMessageData(campaignDetails.data || []);
        setRcsPagination(campaignDetails.pagination);
      }
    }
  }, [campaignDetails, selectedCampaignId, dialogOpen, detailsPage]);

  // Clock update
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Handlers
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(e.target.value);
    setPage(1);
  };

  const handleTemplateView = (id) => {
    setSelectedTemplateId(id);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedTemplateId(null);
  };

  const handleDetailsClick = (campaignId) => {
    navigate(`/rcs-campaign-details/${campaignId}`);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCampaignId(null);
    setDetailsPage(1);
    setCachedRcsCampaign(null);
    setCachedRcsOverview(null);
    setRcsMessageData([]);
    setRcsPagination(null);
    setShouldFetchDetails(false);
  };

  const handleDownloadByCampaignId = async () => {
    if (!selectedCampaignId) {
      toast.error("No campaign selected");
      return;
    }
    try {
      const result = await triggerZipDownload({
        campaign_id: selectedCampaignId,
      }).unwrap();
      const url = window.URL.createObjectURL(result);
      const link = document.createElement("a");
      link.href = url;
      link.download = `campaign_${selectedCampaignId}_report.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Download started!");
    } catch (err) {
      toast.error(err?.data?.message || "Download failed");
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = dayjs(dateString);
    if (!d.isValid()) return "N/A";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
          {d.format("DD/MM/YYYY")}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
          {d.format("HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const maskPhone = (num) => {
    if (!num) return "";
    const str = String(num);
    return str.slice(0, 3) + "****" + str.slice(-3);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "completed":
      case "delivered":
        return "success";
      case "read":
        return "primary";
      case "sent":
        return "info";
      case "failed":
        return "error";
      case "pending":
        return "warning";
      case "in progress":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      {/* HEADER */}
      <Box display="flex" alignItems="center">
        <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold", color: "black" }}>
          RCS Campaign Report
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate("/rcs-component")}
          sx={{
            ml: "auto",
            height: 32,
            fontSize: "0.75rem",
            textTransform: "none",
            backgroundColor: "#0288d1",
            color: "white",
            borderRadius: "8px",
            px: 2,
            boxShadow: 2,
            "&:hover": { backgroundColor: "#0277bd", boxShadow: 4 },
          }}
        >
          + Add Campaign
        </Button>
      </Box>

      {/* RECORD COUNT */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          Showing {rows.length} of {pagination?.total_records || 0} records
        </Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={handleRowsPerPageChange} sx={{ fontSize: "0.75rem" }} disabled={isCampaignFetching}>
            {[25, 50, 100].map((num) => (
              <MenuItem key={num} value={num} sx={{ fontSize: "0.75rem" }}>
                {num}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* TABLE */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 2,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          overflow: "hidden",
          position: "relative",
          minHeight: rows.length === 0 ? 200 : "auto",
        }}
      >
        {isCampaignFetching && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": { color: "white", fontSize: "0.8rem", padding: "8px 12px", fontWeight: 600, borderBottom: "none" },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: 60 }}>Sr</TableCell>
              <TableCell>Campaign Name</TableCell>
              <TableCell>Bot Name</TableCell>
              <TableCell>Template Name</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Executed At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center" sx={{ width: 80 }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <TableRow
                  key={row.campaign_id}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.04)", transition: "background-color 0.2s ease" },
                  }}
                >
                  <TableCell sx={{ fontSize: 13, fontWeight: 500, color: "text.secondary" }}>{row.id}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{row.campaign_name}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: "text.primary" }}>{row.bot_name}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: "text.primary" }}>{row.template_name}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
                    {dayjs(row.created_at).format("DD/MM/YYYY")}
                    <br />
                    {dayjs(row.created_at).format("HH:mm:ss")}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
                    {dayjs(row.executed_at).format("DD/MM/YYYY")}
                    <br />
                    {dayjs(row.executed_at).format("HH:mm:ss")}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={getStatusColor(row.status)}
                      sx={{ fontSize: "0.7rem", height: 24, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="black"
                      size="small"
                      onClick={() => handleDetailsClick(row.campaign_id)}
                      sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)", transform: "scale(1.1)", transition: "all 0.2s ease" } }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="black"
                      size="small"
                      onClick={() => handleTemplateView(row.template_id)}
                      sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)", transform: "scale(1.1)", transition: "all 0.2s ease" } }}
                    >
                      <PhoneAndroidIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {isCampaignFetching ? "Loading campaigns..." : "No campaigns found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINATION */}
      {pagination && pagination.total_pages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={pagination.total_pages || 1}
            page={page}
            color="primary"
            onChange={(e, value) => setPage(value)}
            disabled={isCampaignFetching}
            size="small"
          />
        </Box>
      )}

      {/* CAMPAIGN DETAILS DIALOG */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#0288d1",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Campaign Details - ID: {selectedCampaignId}
          </Typography>
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleDownloadByCampaignId}
              disabled={zipDownloadLoading}
              sx={{
                mr: 1,
                bgcolor: "white",
                color: "#f0f0f0ff",
                "&:hover": { bgcolor: "#baa4cca4" },
              }}
            >
              {zipDownloadLoading ? "Preparing ZIP..." : "Download ZIP"}
            </Button>
            <IconButton onClick={handleCloseDialog} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {(loadingDetails || loadingDetailsFetching) && !cachedRcsCampaign ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
            </Box>
          ) : (cachedRcsCampaign || campaignDetails?.campaign) ? (
            <>
              {/* Campaign Overview Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: "white", fontWeight: "bold", mb: 2, fontSize: "0.95rem" }}>
                  Campaign Overview
                </Typography>
                <Grid container spacing={3}>
                  {/* Campaign Information Card */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: "rgba(255, 255, 255, 0.32)" }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                        Campaign Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Name:</Typography>
                          <Typography variant="body2">{(cachedRcsCampaign || campaignDetails?.campaign)?.campaign_name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Bot:</Typography>
                          <Typography variant="body2">{(cachedRcsCampaign || campaignDetails?.campaign)?.bot_name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Template:</Typography>
                          <Typography variant="body2">{(cachedRcsCampaign || campaignDetails?.campaign)?.template_name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Status:</Typography>
                          <Chip
                            label={(cachedRcsCampaign || campaignDetails?.campaign)?.status}
                            size="small"
                            color={getStatusColor((cachedRcsCampaign || campaignDetails?.campaign)?.status)}
                            sx={{ mt: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Created:</Typography>
                          <Typography variant="body2">{formatDate((cachedRcsCampaign || campaignDetails?.campaign)?.created_at)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Executed:</Typography>
                          <Typography variant="body2">{formatDate((cachedRcsCampaign || campaignDetails?.campaign)?.executed_at)}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Message Statistics Card */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.secondary" }}>
                        Message Statistics
                      </Typography>

                      {/* Submission Section */}
                      <Box sx={{ mb: 1.5, border: "0.5px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 0.75, backgroundColor: "#B8CCE4" }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, color: "#1F3864" }}>
                            Submission : {(cachedRcsOverview || campaignDetails?.overview)?.total ?? 0}
                          </Typography>
                        </Box>
                        <Grid container sx={{ backgroundColor: "rgba(255, 255, 255, 0.32)" }}>
                          {[
                            { label: "Total", value: (cachedRcsOverview || campaignDetails?.overview)?.total },
                            { label: "Submit", value: (cachedRcsOverview || campaignDetails?.overview)?.submit },
                            { label: "Sent", value: (cachedRcsOverview || campaignDetails?.overview)?.sent },
                            { label: "Pending", value: (cachedRcsOverview || campaignDetails?.overview)?.pending },
                          ].map(({ label, value }, i, arr) => (
                            <Grid item xs key={label} sx={{ p: 1.5, borderRight: i < arr.length - 1 ? "0.5px solid" : "none", borderColor: "divider" }}>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>{label}</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500 }}>{value ?? 0}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      {/* Delivered Section */}
                      <Box sx={{ mb: 1.5, border: "0.5px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 0.75, backgroundColor: "#B8CCE4" }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, color: "#1F3864" }}>
                            Delivered : {(cachedRcsOverview || campaignDetails?.overview)?.delivered ?? 0}
                          </Typography>
                        </Box>
                        <Grid container sx={{ backgroundColor: "rgba(255, 255, 255, 0.32)" }}>
                          {[
                            { label: "Delivered", value: (cachedRcsOverview || campaignDetails?.overview)?.delivered },
                            { label: "Read", value: (cachedRcsOverview || campaignDetails?.overview)?.read },
                            { label: "Click", value: (cachedRcsOverview || campaignDetails?.overview)?.click },
                          ].map(({ label, value }, i, arr) => (
                            <Grid item xs key={label} sx={{ p: 1.5, borderRight: i < arr.length - 1 ? "0.5px solid" : "none", borderColor: "divider" }}>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>{label}</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500 }}>{value ?? 0}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      {/* Failed Section */}
                      <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 0.75, backgroundColor: "#B8CCE4" }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, color: "#1F3864" }}>
                            Failed : {(cachedRcsOverview || campaignDetails?.overview)?.failed ?? 0}
                          </Typography>
                        </Box>
                        <Grid container sx={{ backgroundColor: "rgba(255, 255, 255, 0.32)" }}>
                          {[
                            { label: "Disabled", value: (cachedRcsOverview || campaignDetails?.overview)?.disabled },
                            { label: "Limit Ex.", value: (cachedRcsOverview || campaignDetails?.overview)?.limitExhausted },
                            { label: "TTL Expired", value: (cachedRcsOverview || campaignDetails?.overview)?.ttlExpired },
                            { label: "Failed", value: (cachedRcsOverview || campaignDetails?.overview)?.failed },
                            { label: "Rejected", value: (cachedRcsOverview || campaignDetails?.overview)?.rejected },
                            { label: "Other", value: (cachedRcsOverview || campaignDetails?.overview)?.otherFailed },
                          ].map(({ label, value }, i, arr) => (
                            <Grid item xs key={label} sx={{ p: 1.5, borderRight: i < arr.length - 1 ? "0.5px solid" : "none", borderColor: "divider" }}>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>{label}</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500 }}>{value ?? 0}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      {/* Refund Info (if applicable) */}
                      {(campaignDetails?.campaign?.refund_info || cachedRcsCampaign?.refund_info) && (
                        <Box sx={{ mt: 1.5, border: "0.5px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                          <Box sx={{ px: 1.5, py: 0.75, backgroundColor: "#B8CCE4" }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: "#1F3864" }}>
                              Refund Info
                            </Typography>
                          </Box>
                          <Grid container sx={{ backgroundColor: "rgba(255, 255, 255, 0.32)" }}>
                            {(() => {
                              const refund = (campaignDetails?.campaign || cachedRcsCampaign)?.refund_info;
                              return [
                                { label: "Failed Msgs", value: refund?.total_messages_failed },
                                { label: "Rate/Msg (₹)", value: refund?.rate_per_message?.toFixed(2) },
                                { label: "Total Refund (₹)", value: refund?.total_refund_balance?.toFixed(2) },
                              ].map(({ label, value }, i, arr) => (
                                <Grid item xs key={label} sx={{ p: 1.5, borderRight: i < arr.length - 1 ? "0.5px solid" : "none", borderColor: "divider" }}>
                                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>{label}</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 500 }}>{value ?? 0}</Typography>
                                </Grid>
                              ));
                            })()}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Message Details Table */}
              <Typography variant="h6" sx={{ mb: 2, fontSize: "0.95rem", fontWeight: 600 }}>
                Message Details
                {(loadingDetails || loadingDetailsFetching) && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                Total Records: {rcsPagination?.totalRecords || 0}
              </Typography>

              <TableContainer component={Paper} sx={{ borderRadius: 0.5 }}>
                <Table size="small">
                  <TableHead sx={{ background: "#B8CCE4" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Sr No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Send Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Done Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Click</TableCell>
                      {isFailedRefund && <TableCell sx={{ fontWeight: 600 }}>Refund Balance</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ background: "#ffffff6b" }}>
                    {rcsMessageData.length > 0 ? (
                      rcsMessageData.map((row) => (
                        <TableRow key={row.sr_no}>
                          <TableCell>{row.sr_no}</TableCell>
                          <TableCell>{maskPhone(row.user)}</TableCell>
                          <TableCell>{formatDate(row.send_time)}</TableCell>
                          <TableCell>{formatDate(row.done_time)}</TableCell>
                          <TableCell>{row.provider}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.message_status}
                              size="small"
                              color={getStatusColor(row.message_status)}
                              sx={{ fontSize: "0.7rem" }}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                              {row.reason}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setClickReportMobile(row.user);
                                setClickReportPage(1);
                                setClickReportOpen(true);
                              }}
                              title="View click details"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          {isFailedRefund && (
                            <TableCell sx={{ maxWidth: 300 }}>
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                                {row?.refund_rate || 0}
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={isFailedRefund ? 9 : 8} align="center">
                          No message details found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {rcsPagination && rcsPagination.totalPages > 1 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        const newRows = Number(e.target.value);
                        setRowsPerPage(newRows);
                        setDetailsPage(1);
                      }}
                      sx={{ fontSize: "0.8rem", height: 32 }}
                    >
                      {[25, 50, 100].map((num) => (
                        <MenuItem key={num} value={num} sx={{ fontSize: "0.8rem" }}>
                          {num} Rows
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={rcsPagination.totalPages}
                    page={detailsPage}
                    onChange={(_, value) => setDetailsPage(value)}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", py: 4 }}>
              No details available
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* CLICK REPORT DIALOG */}
      <Dialog
        key={clickReportMobile}
        open={clickReportOpen}
        onClose={() => {
          setClickReportOpen(false);
          setClickReportMobile(null);
          setClickReportPage(1);
          setClickReportRowsPerPage(10);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#0288d1",
            color: "white",
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Click Report - {maskPhone(clickReportMobile)}
          </Typography>
          <IconButton onClick={() => setClickReportOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {rcsClickReportLoading || rcsClickReportFetching ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : rcsClickReportError ? (
            <Typography color="white" style={{ fontWeight: "bold" }} textAlign="center" py={4}>
              {rcsClickReportError?.data?.message || "Unknown error"}
            </Typography>
          ) : rcsClickReport?.status && rcsClickReport.data?.length > 0 ? (
            <>
              {/* Campaign Info */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: "rgba(255, 255, 255, 0.32)" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                  Campaign Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={500}>Campaign:</Typography>
                    <Typography variant="body2">{rcsClickReport.campaign_info.campaign_name}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={500}>Template:</Typography>
                    <Typography variant="body2">{rcsClickReport.campaign_info.template_name}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={500}>Agent:</Typography>
                    <Typography variant="body2">{rcsClickReport.campaign_info.agent_name}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Summary */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                  Click Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" fontWeight={500}>Total Clicks</Typography>
                    <Typography variant="h6">{rcsClickReport.summary.total_clicks}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" fontWeight={500}>Unique Buttons</Typography>
                    <Typography variant="h6">{rcsClickReport.summary.unique_buttons}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500}>Last Click</Typography>
                    <Typography variant="body2">{formatDate(rcsClickReport.summary.last_click_time)}</Typography>
                  </Grid>
                </Grid>

                {/* Button Breakdown */}
                {rcsClickReport.summary.button_breakdown?.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Button Breakdown
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Button Text</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Clicks</TableCell>
                            <TableCell>Last Click</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rcsClickReport.summary.button_breakdown.map((btn, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{btn.button_text}</TableCell>
                              <TableCell>{btn.button_type}</TableCell>
                              <TableCell align="center">{btn.total_clicks}</TableCell>
                              <TableCell>{formatDate(btn.last_click_time)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Paper>

              {/* Click Details Table */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Click Details
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Total: {rcsClickReport.pagination.totalRecords} records
                </Typography>
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead sx={{ background: "#B8CCE4" }}>
                    <TableRow>
                      <TableCell>Sr No</TableCell>
                      <TableCell>Button Text</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Click Time</TableCell>
                      <TableCell>Provider</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rcsClickReport.data.map((click) => (
                      <TableRow key={click.sr_no}>
                        <TableCell>{click.sr_no}</TableCell>
                        <TableCell>{click.button_text}</TableCell>
                        <TableCell>{click.button_type}</TableCell>
                        <TableCell>{formatDate(click.click_time)}</TableCell>
                        <TableCell>{click.provider}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {rcsClickReport.pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={clickReportRowsPerPage}
                      onChange={(e) => {
                        setClickReportRowsPerPage(Number(e.target.value));
                        setClickReportPage(1);
                      }}
                      sx={{ fontSize: "0.8rem", height: 32 }}
                    >
                      {[10, 25, 50].map((num) => (
                        <MenuItem key={num} value={num}>
                          {num} Rows
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={rcsClickReport.pagination.totalPages}
                    page={clickReportPage}
                    onChange={(_, value) => setClickReportPage(value)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography textAlign="center" py={4}>
              No click data available for this number.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* TEMPLATE VIEW DIALOG */}
      <Dialog
        key={selectedTemplateId}
        open={viewOpen}
        onClose={handleViewClose}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": { p: 0, backgroundColor: "transparent", boxShadow: "none" },
          "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.5)" },
        }}
      >
        <DialogContent sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "flex-start", pt: 6, backgroundColor: "transparent" }}>
          <Box
            sx={{
              width: "300px",
              minHeight: "400px",
              maxHeight: "75vh",
              backgroundColor: "#fff",
              border: "8px solid #000",
              borderRadius: "32px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            {/* Status Bar */}
            <Box
              sx={{
                width: "100%",
                height: "30px",
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#000",
                color: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "0.75rem" }}>{currentTime}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <WifiIcon sx={{ fontSize: "16px" }} />
                <NetworkCellIcon sx={{ fontSize: "16px" }} />
                <BatteryFullIcon sx={{ fontSize: "16px" }} />
              </Stack>
            </Box>

            <Button
              onClick={handleViewClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                minWidth: 0,
                padding: 0.5,
                zIndex: 10,
                color: "#000",
              }}
            >
              <ClearIcon fontSize="small" />
            </Button>

            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flexGrow: 1,
                backgroundColor: "#f5f5f5",
                minHeight: "300px",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                "&::-webkit-scrollbar-thumb": { bgcolor: "#ccc", borderRadius: "2px", "&:hover": { bgcolor: "#999" } },
              }}
            >
              {templateData?.data ? (
                <Stack spacing={2}>
                  {/* Template Header */}
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem", color: "#000" }}>
                      {templateData.data.templateDetails?.template_name}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#f0f0f0",
                        px: 1,
                        py: 0.3,
                        borderRadius: "4px",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {templateData.data.templateDetails?.rcs_template_type}
                    </Box>
                  </Box>

                  {/* Content Card */}
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                    {(() => {
                      const type = templateData.data.templateDetails?.rcs_template_type;
                      switch (type) {
                        case "Text":
                          return (
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#333", lineHeight: 1.6, fontSize: "0.9rem" }}>
                              {templateData.data.templateDetails?.rcs_text}
                            </Typography>
                          );
                        case "Rich Card":
                          return (
                            <Stack spacing={1.5}>
                              <Box sx={{ borderRadius: "8px", overflow: "hidden", backgroundColor: "#f0f0f0" }}>
                                <img
                                  src={templateData.data.templateDetails?.rich_card_image}
                                  alt="Rich Card"
                                  style={{ width: "100%", borderRadius: "8px", display: "block" }}
                                />
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "#000" }}>
                                  {templateData.data.templateDetails?.rich_card_title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                                  {templateData.data.templateDetails?.rich_card_description}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        case "Carousel":
                          const cards = templateData.data.carousel_cards || [];
                          const selectedCard = cards[selectedCardIndex] || null;
                          return (
                            <Stack spacing={1.5}>
                              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                {cards.map((_, idx) => (
                                  <Button
                                    key={idx}
                                    onClick={() => setSelectedCardIndex(idx)}
                                    sx={{
                                      minWidth: "32px",
                                      height: "32px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      transition: "all 0.3s ease",
                                      ...(selectedCardIndex === idx
                                        ? { backgroundColor: "#000", color: "#fff" }
                                        : { backgroundColor: "#e0e0e0", color: "#333", "&:hover": { backgroundColor: "#ccc" } }),
                                    }}
                                  >
                                    {idx + 1}
                                  </Button>
                                ))}
                              </Box>
                              {selectedCard ? (
                                <Stack spacing={1}>
                                  <Box sx={{ borderRadius: "8px", overflow: "hidden" }}>
                                    <img
                                      src={selectedCard.carousel_image}
                                      alt={`Card ${selectedCard.card_no}`}
                                      style={{ width: "100%", borderRadius: "8px", display: "block" }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "#000" }}>
                                      {selectedCard.carousel_title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                                      {selectedCard.carousel_description}
                                    </Typography>
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography color="text.secondary">No card data available.</Typography>
                              )}
                            </Stack>
                          );
                        default:
                          return <Typography color="text.secondary">Unknown type</Typography>;
                      }
                    })()}
                  </Box>

                  {/* Buttons Card */}
                  {templateData.data.buttonInformation?.length > 0 && (
                    <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                      <Stack spacing={1.5}>
                        {templateData.data.buttonInformation.filter((btn) => Number(btn.button_action_id) === 1).length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem" }}>
                              Suggested Replies
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                              {templateData.data.buttonInformation
                                .filter((btn) => Number(btn.button_action_id) === 1)
                                .map((btn, idx) => (
                                  <Button
                                    key={`action1-${idx}`}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      borderRadius: "6px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      border: "1px solid #ccc",
                                      color: "#333",
                                      transition: "all 0.3s ease",
                                      "&:hover": { backgroundColor: "#f0f0f0", borderColor: "#999" },
                                    }}
                                  >
                                    {btn.suggestion_text}
                                  </Button>
                                ))}
                            </Stack>
                          </Box>
                        )}
                        {templateData.data.buttonInformation.filter((btn) => Number(btn.button_action_id) !== 1).length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem" }}>
                              Suggested Actions
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                              {templateData.data.buttonInformation
                                .filter((btn) => Number(btn.button_action_id) !== 1)
                                .map((btn, idx) => (
                                  <Button
                                    key={`other-${idx}`}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                      borderRadius: "6px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      backgroundColor: "#000",
                                      color: "#fff",
                                      transition: "all 0.3s ease",
                                      "&:hover": { backgroundColor: "#333" },
                                    }}
                                  >
                                    {btn.suggestion_text}
                                  </Button>
                                ))}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <CircularProgress size={30} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RcscampaignList;